import React, { useMemo, useState } from "react";

import { useRouter } from "next/router";
import styled from "styled-components";

import { AnyProduct, offerProducts_offer } from "@api/graphql/types";
import { NormalProduct, productIsDigital } from "@api/graphql/typeHelpers";
import { useLoctool } from "@bigfish/react-loctool";
import { OfferAccordionCard } from "@components/Card/OfferAccordionCard";
import { bp } from "@styles/theme";
import { MediaLibraryHelper } from "@utils/MediaLibraryHelper";
import { Path } from "@utils/Path";

import { AccordionCard } from "./Card/AccordionCard";
import { Flex } from "./Flex";
import Modal from "./Modal";
import { SVGDocument, SVGLaptop } from "./SVGCollection";

interface ProductOfferProps {
    products: AnyProduct[];
    offers?: offerProducts_offer[];
    publicationId: number;
    publicationName: string;
    publicationShortName: string;
    publicationImage: {
        src: string;
        srcSet: string;
    };
    handleModal: (product: NormalProduct) => void;
}

export const ProductOffers: React.FC<ProductOfferProps> = ({
    products,
    offers,
    publicationId,
    publicationName,
    publicationShortName,
    publicationImage,
    handleModal,
}) => {
    const Intl = useLoctool();
    const router = useRouter();
    const [tabActive, setTabActive] = useState(router.query.digital ? 1 : 0);
    const [imageModalActive, setImageModalActive] = useState(false);
    const [currentProductImage, setCurrentProductImage] = useState<string>("");
    const offerProducts = offers?.map(o => o.offerProducts).flat();

    const handleClick = (event: any) => {
        const index = parseInt(event.currentTarget.getAttribute("data-tab"), 0);

        if (index !== tabActive) {
            setTabActive(index);
            if (tabActive === 1) {
                router.replace({ pathname: `${Path.product}/${publicationShortName}`, query: { id: publicationId } }, undefined, { shallow: true });
            } else {
                router.replace({ pathname: `${Path.product}/${publicationShortName}`, query: { id: publicationId, digital: "true" } }, undefined, {
                    shallow: true,
                });
            }
        }
    };

    const printedProducts = useMemo(() => products?.filter(product => productIsDigital(product) === false), [products]);
    const digitalProducts = useMemo(() => products?.filter(product => productIsDigital(product) === true), [products]);
    const printedOffers = useMemo(() => offerProducts?.filter(op => op.publication.id === publicationId && op.product.__typename !== "ProductDigital") ?? [], [
        offerProducts,
    ]);
    const digitalOffers = useMemo(() => offerProducts?.filter(op => op.publication.id === publicationId && op.product.__typename === "ProductDigital") ?? [], [
        offerProducts,
    ]);

    return (
        <>
            {(printedProducts.length > 0 || printedOffers.length > 0) && (digitalProducts.length > 0 || digitalOffers.length > 0) && (
                <Tabs className="_ce_measure_widget" data-ce-measure-widget="termek-tipus-valaszto">
                    <TabPrinted onClick={handleClick} $active={tabActive === 0} data-tab={0}>
                        <SVGDocument />
                        <span>{Intl.formatMessage({ id: "pages.product.printedProduct" })}</span>
                    </TabPrinted>
                    <Separator />
                    <Tab onClick={handleClick} $active={tabActive === 1} data-tab={1}>
                        <SVGLaptop />
                        <span>{Intl.formatMessage({ id: "pages.product.digitalProduct" })}</span>
                    </Tab>
                </Tabs>
            )}
            {(printedProducts.length > 0 || printedOffers.length > 0) && (
                <TabContent $active={tabActive === 0} className="_ce_measure_widget" data-ce-measure-widget="termek-tipus-lista">
                    {printedOffers.map((op, idx) => {
                        return (
                            <OfferAccordionCard
                                key={`printed-offer-${op.id}`}
                                open={idx === 0}
                                group="offer-group0"
                                offerProduct={op}
                                className="_ce_measure_widget"
                            />
                        );
                    })}
                    {printedProducts.map((product, index) => {
                        return (
                            <StyledAccordionCard
                                key={`printed-${product.id}`}
                                open={index === 0}
                                group="offer-group0"
                                product={product}
                                handleModal={handleModal}
                                className="_ce_measure_widget"
                                onImageClick={() => {
                                    if (product.image) {
                                        setCurrentProductImage(MediaLibraryHelper.getMediaLibraryImageSrc(product.image));
                                    } else {
                                        setCurrentProductImage(publicationImage.src);
                                    }

                                    setImageModalActive(true);
                                }}
                            />
                        );
                    })}
                </TabContent>
            )}
            {(digitalProducts.length > 0 || digitalOffers.length > 0) && (
                <TabContent
                    $active={tabActive === 1 || (printedProducts.length === 0 && printedOffers.length === 0)}
                    className="_ce_measure_widget"
                    data-ce-measure-widget="termek-tipus-lista"
                >
                    {digitalOffers.map((op, idx) => {
                        return (
                            <OfferAccordionCard
                                key={`digital-offer-${op.id}`}
                                open={idx === 0}
                                group="offer-group0"
                                offerProduct={op}
                                className="_ce_measure_widget"
                            />
                        );
                    })}
                    {digitalProducts.map((product, index) => {
                        return (
                            <StyledAccordionCard
                                key={`digital-${product.id}`}
                                open={index === 0}
                                group="offer-group0"
                                product={product}
                                handleModal={handleModal}
                                className="_ce_measure_widget"
                                onImageClick={() => {
                                    if (product.image) {
                                        setCurrentProductImage(MediaLibraryHelper.getMediaLibraryImageSrc(product.image));
                                    } else {
                                        setCurrentProductImage(publicationImage.src);
                                    }

                                    setImageModalActive(true);
                                }}
                            />
                        );
                    })}
                </TabContent>
            )}
            <ImageModal
                titleText={publicationName}
                mounted={imageModalActive}
                underlayClickable={true}
                onModalClose={() => setImageModalActive(false)}
                className="_ce_measure_widget"
                data-ce-measure-widget="popup-image-modal"
            >
                <img src={currentProductImage} alt={publicationName} />
            </ImageModal>
        </>
    );
};

const StyledAccordionCard = styled(AccordionCard)`
    box-shadow: none;
    border: 1px solid transparent;
    border-top-color: ${props => props.theme.color.line};
    border-radius: 0;
    z-index: 1;
    transition: border-radius 300ms, border-color 300ms, box-shadow 300ms, z-index 0s 0s;

    &:hover {
        border-color: ${props => props.theme.color.line};
        border-bottom-color: transparent;
        border-radius: ${props => props.theme.radius.medium};
        box-shadow: ${props => props.theme.shadow.card};
        z-index: 2;
    }
`;

const Tabs = styled.div`
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    grid-gap: 14px;
    background-color: ${props => props.theme.color.beigeL};
    margin-bottom: 39px;

    svg {
        min-width: 30px;
        width: 30px;
        margin-right: 14px;
    }

    ${bp.medium} {
        margin-bottom: 30px;
    }
    ${bp.small} {
        margin-bottom: 21px;
    }
`;

const TabContent = styled.div<{ $active: boolean }>`
    transition: opacity 300ms;
    max-height: ${props => (props.$active ? "" : 0)};
    opacity: ${props => (props.$active ? 1 : 0)};

    ${StyledAccordionCard} {
        &:hover {
            border-bottom-color: ${props => props.theme.color.line};

            & + ${StyledAccordionCard} {
                border-color: transparent;
            }
        }
    }

    ${bp.large} {
        width: calc(100% + 40px);
        padding: ${props => (props.$active ? "0 20px 40px" : "0 20px 0")};
        margin-left: -20px;
    }
`;

const Tab = styled(Flex.Container).attrs({
    as: "button",
    $alignItems: "center",
})<{ $active: boolean }>`
    width: 100%;
    padding: 10px 15px;
    ${props => props.$active && `color: ${props.theme.color.red};`}
    font-size: 16px;
    line-height: 24px;
    user-select: none;
`;

const TabPrinted = styled(Tab)`
    svg {
        margin-left: auto;
    }
`;

const Separator = styled.div`
    width: 1px;
    height: 35px;
    margin: auto;
    background-color: ${props => props.theme.color.grayL};
`;

const ImageModal = styled(Modal)`
    width: auto !important;
    height: fit-content;
    padding: 20px;
    border-radius: 0 !important;
    box-shadow: none !important;

    ${bp.large} {
        padding: 0;
    }

    img {
        width: 100%;
        box-shadow: 0px 2px 10px rgb(0 0 0 / 10%);
    }
`;
